import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ProductRequest, ProductResponse } from '../../../@types/product/product.type';
import axiosInstance from '../../../utils/axios';

interface RequestState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface ProductState {
  single: RequestState<ProductResponse>;
  list: RequestState<ProductResponse[]>;
  create: RequestState<ProductResponse>;
  update: RequestState<ProductResponse>;
  delete: RequestState<string>;
}

const initialRequestState = <T,>(): RequestState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: ProductState = {
  single: initialRequestState<ProductResponse>(),
  list: initialRequestState<ProductResponse[]>(),
  create: initialRequestState<ProductResponse>(),
  update: initialRequestState<ProductResponse>(),
  delete: initialRequestState<string>(),
};

const BASE_URL = '/api/v1/products';

// ---------- Thunks ----------

export const fetchProductById = createAsyncThunk(
  'products/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}`);
      return res.data as ProductResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const fetchProductsByOrgId = createAsyncThunk(
  'products/fetchByOrganizationId',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/organization/${organizationId}?size=100`);
      return res.data.content as ProductResponse[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const fetchProductsFromPosByOrgId = createAsyncThunk(
  'products/fetchFromPosByOrganizationId',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${organizationId}/posProducts`);
      return res.data.content as ProductResponse[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/create',
  async (data: FormData, { rejectWithValue }) => {
    try {
      console.log('product before creation: ', data)
      const res = await axiosInstance.post(BASE_URL, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('product after creation: ', res)
      return res.data as ProductResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Create failed');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/update',
  async ({ id, data }: { id: string; data: FormData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`${BASE_URL}/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data as ProductResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Update failed');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Delete failed');
    }
  }
);

// ---------- Slice ----------

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearCreateProductState: (state) => {
      state.create = initialRequestState();
    },
    clearUpdateProductState: (state) => {
      state.update = initialRequestState();
    },
    clearDeleteProductState: (state) => {
      state.delete = initialRequestState();
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch one
      .addCase(fetchProductById.pending, (state) => {
        state.single.loading = true;
        state.single.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.single.loading = false;
        state.single.data = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.single.loading = false;
        state.single.error = action.payload as string;
      })

      // Fetch all by org
      .addCase(fetchProductsByOrgId.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchProductsByOrgId.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchProductsByOrgId.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })

      // Fetch all from pos by org
      .addCase(fetchProductsFromPosByOrgId.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchProductsFromPosByOrgId.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchProductsFromPosByOrgId.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })

      // Create
      .addCase(createProduct.pending, (state) => {
        state.create.loading = true;
        state.create.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.create.loading = false;
        state.create.data = action.payload;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.create.loading = false;
        state.create.error = action.payload as string;
      })

      // Update
      .addCase(updateProduct.pending, (state) => {
        state.update.loading = true;
        state.update.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.update.loading = false;
        state.update.data = action.payload;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.update.loading = false;
        state.update.error = action.payload as string;
      })

      // Delete
      .addCase(deleteProduct.pending, (state) => {
        state.delete.loading = true;
        state.delete.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.delete.loading = false;
        state.delete.data = action.payload;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.delete.loading = false;
        state.delete.error = action.payload as string;
      });
  },
});

export const {
  clearCreateProductState,
  clearUpdateProductState,
  clearDeleteProductState,
} = productsSlice.actions;

export default productsSlice.reducer;
